import { RESULT_NARRATIVES } from "@/components/assessment/resultNarratives";
import { getRobustEducationalModules } from "@/components/assessment/educationalModules";

const atLeast = (value, threshold) =>
  Number.isFinite(value) && value >= threshold;

const atMost = (value, threshold) =>
  Number.isFinite(value) && value <= threshold;

const fallbackNarrative = RESULT_NARRATIVES["regulation-building"];

const selectedOrFallback = (selected, fallback) =>
  Array.isArray(selected) && selected.length ? selected : fallback;

const naturalList = (items = [], conjunction = "and") => {
  const values = items.filter(Boolean);
  if (!values.length) return "changes that are difficult to name";
  if (values.length === 1) return values[0];
  if (values.length === 2) return `${values[0]} ${conjunction} ${values[1]}`;
  return `${values.slice(0, -1).join(", ")}, ${conjunction} ${values.at(-1)}`;
};

const system = (name, role) => ({ name, role });

const mobilizationStory = (scores) => {
  if (atLeast(scores.mobilizationDemand, 3.7)) {
    return "The sympathetic branch of your autonomic nervous system may recruit energy, muscle tension, circulation, and action quickly when demand rises. Your central autonomic network—the brain system connecting internal sensation, relevance, emotion, and automatic body control—helps coordinate that action-ready shift before you consciously choose what to do.";
  }
  if (atMost(scores.mobilizationDemand, 2.4)) {
    return "Arousal and action networks in your central nervous system may need more time, a clearer reason, or a lower entry cost before they recruit your body for action. Beginning can therefore feel harder than continuing once movement, light, interest, or a defined task has created momentum.";
  }
  return "Your central autonomic network appears to scale mobilization according to context. The sympathetic branch of your autonomic nervous system may create useful energy in some situations, while uncertainty, low resources, or competing signals make the response slower or less predictable in others.";
};

const controlStory = (scores) => {
  if (atMost(scores.control, 2.7)) {
    return "When sympathetic mobilization rises, prefrontal executive networks in your central nervous system may have less usable capacity for working memory, inhibition, and flexible switching. That can make attention feel captured and reduce the number of choices that feel available, even when part of you knows another response would be useful.";
  }
  if (atLeast(scores.control, 3.8)) {
    return "Your prefrontal executive networks appear to stay meaningfully available while the autonomic nervous system mobilizes. You may still feel a strong reaction in your body, but there is often enough central-nervous-system capacity to pause, redirect attention, stop, or use the energy deliberately.";
  }
  return "Your prefrontal executive networks appear more available in some conditions than others. Familiarity, pressure, energy, environment, and support may determine whether the central autonomic network can coordinate body activation while leaving enough capacity for flexible attention and choice.";
};

const recoveryStory = (scores) => {
  if (atMost(scores.recovery, 2.7) || atMost(scores.resources, 2.7)) {
    return "The return after demand may be the more difficult part of your cycle. Sympathetic drive may withdraw slowly, while parasympathetic pathways—including vagal pathways linking the brainstem with the heart, lungs, and digestive organs—may need more time and recovery resources to help breathing, tension, attention, and usable energy settle.";
  }
  if (atLeast(scores.recovery, 3.8) && atLeast(scores.resources, 3.8)) {
    return "Your autonomic nervous system appears able to shift from sympathetic mobilization toward parasympathetic recovery with useful reliability. After demand passes, breathing, tension, attention, and energy may return toward a workable range without unusually long carryover.";
  }
  return "Your autonomic recovery appears context-dependent. Parasympathetic and vagal pathways may help you settle after manageable or familiar demands, while accumulated pressure, poor sleep, limited nourishment, or several demands at once can keep sympathetic activation or fatigue present for longer.";
};

const contextStory = (profile) => {
  const { scores, vector } = profile;
  if (
    atLeast(vector.environment.sensitivity, 4) &&
    atMost(vector.environment.control, 3)
  ) {
    return "Your central autonomic network appears especially responsive to environmental input. Noise, interruption, crowding, screens, unpredictability, or limited control over the setting may increase salience and sympathetic demand before the task itself is considered.";
  }
  if (atLeast(vector.connection.socialEvaluationSensitivity, 4)) {
    return "Your central autonomic network may treat social evaluation as added demand. Being watched, compared, evaluated, or involved in conflict can increase sympathetic mobilization and make prefrontal attention, behavioural control, or parasympathetic recovery less predictable.";
  }
  if (atMost(scores.resources, 2.7)) {
    return "Your autonomic response is being shaped by the resources surrounding it. Sleep, nourishment, hydration, routine fit, and actual recovery opportunity influence how much demand the central nervous system can organize before attention, energy, or recovery becomes less flexible.";
  }
  return "Your central autonomic network keeps integrating the setting, body signals, memory, meaning, and available support. Familiar environments, predictable routines, supportive people, and activities you can control may therefore help the same autonomic nervous system respond very differently.";
};

const recentChangeStory = (temporalContext = {}) => {
  const change = {
    harder: "You have noticed this response becoming stronger or harder to manage recently, which can happen when total demand rises faster than recovery or available support.",
    easier: "You have noticed parts of this response becoming easier to manage recently, suggesting that some combination of capacity, support, practice, or recovery is already helping.",
    mixed: "You have noticed a mixed change: some parts have improved while others have become harder. That often means the response is sensitive to timing, context, or the resources available on a given day.",
    none: "You have not noticed a clear recent change, so this may be a relatively familiar pattern rather than a short-lived shift.",
    unknown: "You were not yet sure whether this pattern has changed recently. Watching when it appears, what precedes it, and how it resolves can make the pattern easier to understand.",
  }[temporalContext.recentChange];
  return change || "Your recent experience provides context for how this pattern is showing up now.";
};

const dailyLifeMode = (profile) => {
  const scores = profile?.scores || {};
  const vector = profile?.vector || {};
  const environmental =
    atLeast(vector.environment?.sensitivity, 4) &&
    atMost(vector.environment?.control, 3);

  if (environmental) return "environmental";
  if (
    atLeast(scores.mobilizationDemand, 3.7) &&
    atMost(scores.availableEnergy, 2.4)
  ) {
    return "activated-depleted";
  }
  if (
    atLeast(scores.mobilizationDemand, 3.7) &&
    atMost(scores.control, 2.7)
  ) {
    return "narrowed";
  }
  if (
    atLeast(scores.mobilizationDemand, 3.7) &&
    (atMost(scores.recovery, 2.7) || atMost(scores.resources, 2.7))
  ) {
    return "recovery-limited";
  }
  if (
    atLeast(scores.control, 3.8) &&
    atLeast(scores.recovery, 3.8) &&
    atLeast(scores.capacity, 3.8)
  ) {
    return "flexible";
  }
  if (atMost(scores.mobilizationDemand, 2.4)) return "slow-start";
  if (atMost(scores.recovery, 2.7)) return "slow-recovery";
  return "variable";
};

const overviewFor = ({ mode, emotional, physical, cognitive, behavioral }) => {
  const signalSentence = `Feeling ${naturalList(emotional)}, noticing ${naturalList(
    physical,
  )}, and experiencing ${naturalList(
    cognitive,
  )} while you are more likely to ${naturalList(
    behavioral,
    "or",
  )} may not be separate problems.`;

  const explanations = {
    environmental:
      "Together, they fit a response in which environmental stimulation is adding demand faster than it can be sorted and down-regulated. Your central autonomic network may keep assigning importance to noise, light, screens, interruption, crowding, or unpredictability; sympathetic mobilization and attentional filtering can then remain active even when your physical capacity is otherwise available.",
    "activated-depleted":
      "Together, they form a strong match for a system reducing overall output while it manages ongoing demand, limited usable energy, or incomplete recovery. Emotional intensity may become quieter, the body may feel slower or heavier, thinking may require more effort, and additional demands may feel easier to avoid. This is not necessarily a lack of motivation; it may be your central nervous system limiting further demand until more usable capacity is available.",
    narrowed:
      "Together, they fit strong sympathetic mobilization with less room for flexible control once the response is underway. Your central autonomic network may prioritize rapid detection and action, while prefrontal executive networks have less usable capacity for working memory, inhibition, and switching. The result can feel urgent, physical, emotionally intense, and difficult to redirect.",
    "recovery-limited":
      "Together, they fit a response that can mobilize for demand but does not always complete the return afterward. Sympathetic drive may remain present while parasympathetic and vagal recovery develops more slowly, allowing tension, fatigue, mental carryover, or irritability to continue after the original demand has ended.",
    flexible:
      "Together, they suggest that your system can usually recruit activation without losing meaningful choice, then return toward recovery. Strong emotion, a noticeable heartbeat, or intense focus can still occur; the important difference is that prefrontal control and parasympathetic recovery generally remain available enough for the response to stay useful.",
    "slow-start":
      "Together, they fit a system that may be capable of activity but slower to recruit arousal and action. Central nervous system arousal networks may need a clearer cue, lower entry cost, movement, light, interest, or social support before energy and attention organize around the task. Difficulty beginning is not the same as an absence of ability.",
    "slow-recovery":
      "Together, they fit a response that continues after the demand itself is over. Sympathetic withdrawal and parasympathetic recovery may take longer, while the central autonomic network continues carrying body sensation, attention, or emotional meaning from what happened. The lingering state can make the next ordinary demand feel larger than it otherwise would.",
    variable:
      "Together, they fit a context-dependent response rather than one fixed state. Your central autonomic network continually integrates body signals, environment, memory, meaning, energy, and support; changes in any of those inputs may shift whether your autonomic nervous system mobilizes, narrows, withdraws, or recovers.",
  };

  return [signalSentence, explanations[mode] || explanations.variable];
};

const emotionContent = ({ emotional, highMobilization, lowOutput }) => {
  let explanation;
  if (lowOutput) {
    explanation =
      "Your central autonomic network links internal body sensation with emotional meaning. When usable energy or recovery is limited, that network may reduce the intensity or range of emotion available to awareness. Feeling low, flat, or withdrawn can therefore arrive with heaviness and reduced initiation rather than as a separate failure of motivation.";
  } else if (highMobilization) {
    explanation =
      "The central autonomic network includes the insula, anterior cingulate, amygdala, hypothalamus, and connected brainstem regions. When it identifies demand as important, it can coordinate sympathetic mobilization with stronger emotional salience. Anxiety, irritability, overwhelm, or mixed feelings can intensify because the brain and body are preparing for action together.";
  } else {
    explanation =
      "Your central autonomic network continuously combines body sensation, context, memory, and meaning. That is why emotion may change with fatigue, environment, social safety, or task pressure even when the external situation looks similar. The feeling is part of a coordinated response, not an isolated event.";
  }

  const supports = lowOutput
    ? [
        "Lower the expectation to explain, perform, or solve everything before seeking support.",
        "Choose low-demand connection: quiet company, a short text, or walking beside someone can provide contact without requiring a full conversation.",
        "Use a small, familiar input—natural light, food or fluid when needed, music, or five to ten minutes of easy movement—to create a gentle shift in available energy.",
      ]
    : highMobilization
      ? [
          "Lower one source of urgency before trying to reason through every feeling: reduce noise, step away from evaluation, or pause incoming messages.",
          "Give the activation a controlled direction through familiar rhythmic movement or another simple, stoppable physical task.",
          "Use connection that feels regulating rather than demanding, such as a calm voice, quiet company, or a brief walk with someone you trust.",
        ]
      : [
          "Name the strongest feeling and the body signal arriving with it; pairing the two can make the response easier to recognize earlier.",
          "Adjust the setting or demand before assuming you need to force a different emotion.",
          "Repeat a familiar support that has helped under similar conditions, especially when it is easy to begin and recover from.",
        ];

  return {
    key: "emotion",
    title: "Emotion",
    kicker: "What the response may feel like",
    summary: `If you have been feeling ${naturalList(
      emotional,
    )}, those feelings may be the emotional side of the same coordinated response affecting your body, attention, and actions.`,
    signals: emotional,
    explanation,
    systems: [
      system(
        "Central autonomic network (CAN)",
        "Connects internal sensation, emotional meaning, relevance, and automatic body regulation.",
      ),
      system(
        highMobilization
          ? "Sympathetic branch of the autonomic nervous system"
          : "Autonomic nervous system",
        highMobilization
          ? "Raises action readiness and can increase the intensity or urgency of emotion."
          : "Coordinates automatic body changes that help shape how an emotion feels.",
      ),
      system(
        "Prefrontal and limbic networks",
        "Help interpret context, regulate emotion, and keep flexible choice available.",
      ),
    ],
    questions: [
      "What happened in the 30 to 60 minutes before the feeling changed?",
      "Does it intensify with conflict, evaluation, noise, uncertainty, hunger, or fatigue?",
      "In that moment, do you need less demand, movement, food or fluid, privacy, or connection?",
    ],
    supports,
  };
};

const bodyContent = ({ physical, highMobilization, lowOutput, slowRecovery }) => {
  const hasDigestiveSignal = physical.some((item) =>
    /digest|stomach|nausea|bowel/i.test(item),
  );
  let explanation = highMobilization
    ? "Sympathetic autonomic output can increase muscle recruitment, sweating, heart activity, and breathing changes so the body is ready to act. If the demand continues or recovery is delayed, these signals can remain noticeable after they are no longer useful."
    : lowOutput
      ? "Heaviness and fatigue can appear when central arousal, available energy, and recovery resources are not aligning with current demand. This can involve central nervous system and autonomic regulation without proving that one parasympathetic or vagal branch is solely responsible."
      : slowRecovery
        ? "Your body may complete the task before the autonomic cycle has fully settled. Sympathetic drive can withdraw gradually while parasympathetic and vagal pathways help heart rate, breathing, digestion, and muscular tension return toward their working ranges."
        : "The autonomic nervous system continually adjusts circulation, breathing, temperature, muscle readiness, and digestion. These physical changes can be useful responses to demand, but they become more noticeable when the input is intense, prolonged, unfamiliar, or difficult to control.";

  if (hasDigestiveSignal) {
    explanation +=
      " Digestive changes also involve the enteric nervous system, sympathetic output, and parasympathetic signaling carried largely through the vagus nerve, which is why stress, meals, hydration, and recovery can all change what you notice in your stomach or gut.";
  }

  const systems = [
    system(
      "Autonomic nervous system (ANS)",
      "Automatically adjusts heart activity, breathing, sweating, circulation, temperature control, and digestion.",
    ),
    system(
      highMobilization
        ? "Sympathetic branch"
        : "Parasympathetic and vagal pathways",
      highMobilization
        ? "Recruits the body for action through changes in cardiovascular, respiratory, sweat, and muscle responses."
        : "Support the transition toward restoration and influence the heart, lungs, and digestive organs.",
    ),
  ];
  systems.push(
    hasDigestiveSignal
      ? system(
          "Enteric nervous system and brain–gut pathways",
          "Coordinate digestive function and exchange information with the brain through neural, hormonal, and immune routes.",
        )
      : system(
          "Central autonomic network (CAN)",
          "Coordinates brain interpretation with the automatic body response appropriate to the situation.",
        ),
  );

  return {
    key: "body",
    title: "Body",
    kicker: "Physical signals in the same response",
    summary: `If you have noticed ${naturalList(
      physical,
    )}, these may be the body side of the same response—not unrelated signals arriving at random.`,
    signals: physical,
    explanation,
    systems,
    questions: [
      "Which physical signal tends to appear first, and which one tells you the response is ending?",
      "Does it change with sleep, meals, hydration, caffeine, pain, temperature, or the time of day?",
      "Does the signal settle when the environment changes, the demand ends, or your body moves?",
    ],
    supports: [
      slowRecovery
        ? "Build a visible ending into effort: reduce pace gradually and allow breathing and muscle tension time to settle."
        : "Use familiar, rhythmic movement at a dose you can stop easily; the goal is organized input, not exhaustion.",
      "Reduce one source of physical demand—heat, noise, restrictive positioning, missed food or fluid, or unnecessary intensity—when it applies.",
      hasDigestiveSignal
        ? "Use regular nourishment, hydration, and lower-demand movement where appropriate; persistent digestive changes deserve their own health assessment."
        : "Support the next recovery window with hydration, nourishment, comfortable movement, and sufficient lower-demand time.",
    ],
    safetyNote:
      "If a physical signal is new, severe, worsening, or concerning, treat it as a health question rather than assuming it is only part of a stress response.",
  };
};

const cognitiveContent = ({
  cognitive,
  highMobilization,
  lowControl,
  lowOutput,
  environmental,
}) => {
  let explanation;
  if (highMobilization && lowControl) {
    explanation =
      "When the central autonomic network prioritizes rapid detection and action, sympathetic activation can rise while dorsolateral prefrontal and other executive networks have less usable capacity for working memory, inhibition, and flexible switching. That can make tunnel vision, repetitive thought, indecision, or mental fog feel biologically consistent with the state you are in.";
  } else if (lowOutput) {
    explanation =
      "When usable energy or recovery is limited, the central nervous system may devote more capacity to monitoring internal demand and maintaining essential function. Executive processes such as holding options in mind, comparing them, and initiating a choice can then require more effort, which may feel like fog or indecision rather than a lack of intelligence or concern.";
  } else if (environmental) {
    explanation =
      "Your central autonomic and salience networks may keep tagging environmental signals as important. Executive attention then has more information to filter, leaving fewer resources for working memory and flexible switching. Mental fog and tunnel vision can therefore alternate: one reflects overload, while the other reflects attention narrowing around what seems most urgent.";
  } else {
    explanation =
      "Executive attention is shaped by central nervous system arousal, body signals, context, and recovery. When those inputs are well matched, focus can be strong and useful. When they compete, switching, prioritizing, or holding several options in mind may become temporarily harder.";
  }

  return {
    key: "thinking",
    title: "Thinking and focus",
    kicker: "Why clarity and decisions may change",
    summary: `If ${naturalList(
      cognitive,
    )} have been showing up lately, they may make sense when your brain is allocating more capacity to demand, internal signals, or recovery and less to flexible thinking.`,
    signals: cognitive,
    explanation,
    systems: [
      system(
        "Central nervous system executive networks",
        "Support working memory, inhibition, planning, decision-making, and switching between tasks or perspectives.",
      ),
      system(
        "Dorsolateral prefrontal cortex",
        "Contributes to working memory and flexible control; acute stress can temporarily reduce how efficiently this region supports those tasks.",
      ),
      system(
        "Central autonomic and salience networks",
        "Decide which body or environmental signals deserve priority and help direct attention toward them.",
      ),
    ],
    questions: [
      "Does the fog or tunnel vision rise when you are tense, tired, hungry, overstimulated, evaluated, or rushed?",
      "Which decisions stay easy in this state, and which require holding too many options in mind?",
      "What reliably restores clarity: quiet, sleep, food or fluid, movement, completion, or support?",
    ],
    supports: [
      "Reduce the number of live inputs: silence notifications, move to a quieter setting, or close tasks that are not part of the next decision.",
      "Externalize the decision by writing the two most realistic options and choosing the smallest reversible next step.",
      lowOutput
        ? "Use a brief, low-cost reset—food or fluid when needed, natural light, or five to ten minutes of easy movement—before returning to the decision."
        : "Use five to ten minutes of familiar rhythmic movement or a clear physical task, then return to one decision rather than the entire problem.",
    ],
  };
};

const behaviorContent = ({
  behavioral,
  highMobilization,
  lowOutput,
  environmental,
}) => {
  let explanation;
  if (lowOutput) {
    explanation =
      "Withdrawal, stillness, or seeking support can be lower-cost ways of limiting further demand when usable capacity feels scarce. Wanting connection while also withdrawing is not a contradiction: your system may want the safety of another person without having energy for conversation, explanation, performance, or additional expectations.";
  } else if (highMobilization) {
    explanation =
      "When sympathetic activation rises, the central autonomic network can bias action toward the fastest available route—approach, confrontation, escape, movement, or immediate problem-solving. These directions may protect control in the short term, but they can become less useful when urgency decides before prefrontal networks have time to compare options.";
  } else if (environmental) {
    explanation =
      "Leaving, reducing contact, or continuing while activated may be attempts to regain control over incoming stimulation. The behaviour makes more sense when viewed as an effort to change autonomic demand, not simply as avoidance or inconsistency.";
  } else {
    explanation =
      "Behaviour is the action side of the coordinated response. The central nervous system selects from the options that feel available at that moment, while autonomic activation, energy, environment, learning history, and support influence which option feels easiest or safest to use.";
  }

  return {
    key: "behaviour",
    title: "Behaviour and action",
    kicker: "Why your next move may feel automatic",
    summary: `If you have been more likely to ${naturalList(
      behavioral,
      "or",
    )}, the action may be your system's quickest available way to manage demand, regain control, conserve energy, or find support.`,
    signals: behavioral,
    explanation,
    systems: [
      system(
        "Central autonomic network (CAN)",
        "Links perceived demand and internal state with a coordinated action tendency.",
      ),
      system(
        "Prefrontal executive and motor networks",
        "Compare options, inhibit a first impulse, organize movement, and carry out a chosen response.",
      ),
      system(
        "Autonomic nervous system",
        "Changes action readiness and influences whether approach, escape, stillness, or recovery feels most available.",
      ),
    ],
    questions: [
      "What does this action help you escape, complete, control, conserve, or receive in the moment?",
      "What usually happens in your body just before the action begins?",
      "Would connection help if it required no explaining, fixing, or performing?",
    ],
    supports: [
      "Make the next action smaller, familiar, and easy to stop so choice can remain available while the state changes.",
      environmental
        ? "Change the sensory environment first—lower noise, light, interruption, crowding, or screen input—when that is the clearest source of demand."
        : "Create a brief pause between urge and action by changing rooms, standing up, or naming the next two realistic options.",
      lowOutput
        ? "Ask for low-demand support: quiet company, a practical task, or a short walk can provide connection without a full conversation."
        : "Direct action-ready energy into one purposeful physical task, then include a deliberate reduction in pace rather than stopping at peak activation.",
    ],
  };
};

const buildDailyLife = (profile, legacy, expression) => {
  const emotional = selectedOrFallback(expression.emotions, legacy.signs.slice(0, 3));
  const physical = selectedOrFallback(
    expression.bodySignals,
    legacy.recoveryChallenges.slice(0, 3),
  );
  const cognitive = selectedOrFallback(
    expression.cognitiveSignals,
    ["attention or mental clarity may change as demand rises"],
  );
  const behavioral = selectedOrFallback(
    expression.reactionDirections,
    legacy.behaviouralOutputs.slice(0, 3),
  );
  const scores = profile?.scores || {};
  const mode = dailyLifeMode(profile);
  const shared = {
    mode,
    highMobilization: atLeast(scores.mobilizationDemand, 3.7),
    lowControl: atMost(scores.control, 2.7),
    lowOutput: mode === "activated-depleted" || mode === "slow-start",
    slowRecovery: mode === "recovery-limited" || mode === "slow-recovery",
    environmental: mode === "environmental",
  };

  return {
    mode,
    overview: overviewFor({ mode, emotional, physical, cognitive, behavioral }),
    plasticitySnippet:
      "Your ability to change this pattern depends partly on neuroplasticity—the central nervous system's ability to strengthen, weaken, and reorganize response pathways through repeated experience. BDNF is one protein involved in synaptic plasticity, learning, memory, and neuronal health. Recoverable movement, sleep, and repeated practice can help create conditions that support plasticity; BDNF is one contributor, not a single explanation for feeling low, foggy, anxious, or withdrawn.",
    vagalClarifier:
      "Vagal pathways are part of the parasympathetic autonomic nervous system. The vagus nerve carries signals between brainstem circuits and organs including the heart, lungs, and digestive tract. A low-energy or withdrawn feeling alone does not identify a single ‘dorsal vagal’ state; similar outward experiences can develop through several central, autonomic, metabolic, and contextual routes.",
    emotional,
    physical,
    cognitive,
    behavioral,
    possiblePatterns: legacy.behaviouralOutputs,
    sections: [
      emotionContent({ ...shared, emotional }),
      bodyContent({ ...shared, physical }),
      cognitiveContent({ ...shared, cognitive }),
      behaviorContent({ ...shared, behavioral }),
    ],
    researchStartingPoints: [
      "Central autonomic network and autonomic regulation",
      "Sympathetic and parasympathetic nervous-system function",
      "Acute stress, dorsolateral prefrontal cortex, and working memory",
      "Vagus nerve, enteric nervous system, and brain–gut signaling",
      "BDNF, exercise, and neuroplasticity",
    ],
    sources: [
      "Sklerov, Dayan & Browner 2019 — functional neuroimaging of the central autonomic network (PMID 30470943)",
      "Benarroch 2020 — physiology of the sympathetic, parasympathetic, and enteric nervous systems (PMID 31996619)",
      "Qin et al. 2009 — acute stress and working-memory activity in the dorsolateral prefrontal cortex (PMID 19403118)",
      "Yang et al. 2020 — BDNF, neural plasticity, and mood-related research (PMID 32351365)",
    ],
  };
};

export function buildRobustClientNarrative(profile) {
  const legacy =
    RESULT_NARRATIVES[profile?.legacyProfileKey] || fallbackNarrative;
  const expression = profile?.expression || {};
  const temporalContext = profile?.temporalContext || {};

  return {
    stateName: legacy.stateName,
    stateDescription: legacy.stateDescription,
    nervousSystemStory: [
      {
        key: "mobilization",
        title: "How your system prepares for action",
        body: mobilizationStory(profile.scores),
      },
      {
        key: "control",
        title: "What happens to choice and attention",
        body: controlStory(profile.scores),
      },
      {
        key: "recovery",
        title: "How the reaction ends",
        body: recoveryStory(profile.scores),
      },
      {
        key: "context",
        title: "Why context can change the result",
        body: contextStory(profile),
      },
    ],
    priorities: legacy.priorities,
    whyThisMayBeHappening: [
      recentChangeStory(temporalContext),
      legacy.whyThisMatters,
      "This pattern does not define your personality. It may reflect what your central nervous system and central autonomic network have repeatedly coordinated in response to demand, the sympathetic or parasympathetic pathways most useful in those moments, the resources available, and what helped you keep functioning or find relief.",
    ],
    reportedDemand: selectedOrFallback(
      profile.contributors,
      ["No single condition was strongly identified as the main source of demand."],
    ),
    reportedSupport: selectedOrFallback(
      profile.protectiveFactors,
      ["Future before-to-after observations can help identify what reliably supports you."],
    ),
    dailyLife: buildDailyLife(profile, legacy, expression),
    changeFocus: legacy.focus,
    scienceModules: getRobustEducationalModules({ profile }),
  };
}
